import client from "@/client/publicApiClient.ts";
import { useMutation } from "@tanstack/react-query";
import type { CreateClientInvitationMutationRequest, CreateClientInvitationMutationResponse, CreateClientInvitationPathParams, CreateClientInvitation400 } from "../types/CreateClientInvitation";
import type { UseMutationOptions } from "@tanstack/react-query";

 type CreateClientInvitationClient = typeof client<CreateClientInvitationMutationResponse, CreateClientInvitation400, CreateClientInvitationMutationRequest>;
type CreateClientInvitation = {
    data: CreateClientInvitationMutationResponse;
    error: CreateClientInvitation400;
    request: CreateClientInvitationMutationRequest;
    pathParams: CreateClientInvitationPathParams;
    queryParams: never;
    headerParams: never;
    response: CreateClientInvitationMutationResponse;
    client: {
        parameters: Partial<Parameters<CreateClientInvitationClient>[0]>;
        return: Awaited<ReturnType<CreateClientInvitationClient>>;
    };
};
/**
 * @description Creates an invitation to a vlient for a given email address. If an email
address is already invited to the client then the invitation will be
revoked and a new one will be created.

This API call results in an email being sent to the given email address.

If the email address already belongs to a user for the client then this will
return a validation error.
 * @summary Create
 * @link /api/clients/:clientCode/invitations
 */
export function useCreateClientInvitation(clientCode: CreateClientInvitationPathParams["clientCode"], options: {
    mutation?: UseMutationOptions<CreateClientInvitation["response"], CreateClientInvitation["error"], CreateClientInvitation["request"]>;
    client?: CreateClientInvitation["client"]["parameters"];
} = {}) {
    const { mutation: mutationOptions, client: clientOptions = {} } = options ?? {};
    return useMutation({
        mutationFn: async (data) => {
            const res = await client<CreateClientInvitation["data"], CreateClientInvitation["error"], CreateClientInvitation["request"]>({
                method: "post",
                url: `/api/clients/${clientCode}/invitations`,
                data,
                headers: { "Content-Type": "text/json", ...clientOptions.headers },
                ...clientOptions
            });
            return res.data;
        },
        ...mutationOptions
    });
}